import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/LanguageContext";
import { formatDateForLang } from "../../lib/dateFormat";
import { formatHebrewDateEn } from "../../lib/hebrewGematria";
import { Clock, MapPin, Calendar as CalIcon, Star, Sparkles, Flame, Moon, Sun, Plus, Loader2, Trash2, Pencil, AlertCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import EventForm from "./EventForm";
import LocationPicker from "./LocationPicker";
import { toast } from "sonner";

const GREG_HE = [
  "ינואר","פברואר","מרץ","אפריל","מאי","יוני",
  "יולי","אוגוסט","ספטמבר","אוקטובר","נובמבר","דצמבר",
];
const WEEKDAY_HE = ["ראשון","שני","שלישי","רביעי","חמישי","שישי","שבת"];

// Display order — the canonical 14 from the backend, organised to read
// top-to-bottom like a printed luach.
const ZMANIM_DAY = [
  "alotHaShachar", "misheyakir", "sunrise",
  "sofZmanShmaMGA", "sofZmanShmaGRA",
  "sofZmanTfillaMGA", "sofZmanTfillaGRA",
  "chatzot", "minchaGedola", "minchaKetana",
  "plagHaMincha", "sunset", "tzeit", "rabbeinuTam",
];

const CATEGORY_ICON = {
  "major-holiday":  Star,
  "minor-holiday":  Sparkles,
  "fast-day":       Flame,
  "rosh-chodesh":   Moon,
  "modern":         Sun,
  "parsha":         CalIcon,
  "erev":           Sparkles,
};

const COLOR_DOT = {
  gold:   "bg-gold/80",
  navy:   "bg-[#1E2A3A]",
  green:  "bg-emerald-600",
  red:    "bg-rose-600",
  purple: "bg-violet-600",
};

function ZmanRow({ label, value, testid }) {
  return (
    <div
      className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0 text-sm"
      data-testid={testid}
    >
      <span className="font-mono text-foreground tracking-tight">
        {value || "—"}
      </span>
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}

/**
 * Hebrew label for the candle-lighting row.
 *
 * Uses the backend's classification:
 *   `candles_for`  ∈ {"shabbat", "yomtov", "shabbat_yomtov"}
 *   `yomtov_he`    — Hebrew name of the chag (e.g. "שבועות")
 *
 * Why: showing "כניסת שבת" on a weekday Erev Chag (e.g. Thursday Erev
 * Shavuot) is a halachic mislabel — the user is lighting for chag,
 * not Shabbat. Falls back to "כניסת שבת" if the backend hasn't
 * surfaced the classification (older deploy / unknown locale).
 */
function entryLabel(shabbat, t) {
  if (!shabbat) return t("day_detail.candle_lighting_shabbat");
  const kind = shabbat.candles_for;
  const chagHe = shabbat.yomtov_he;
  if (kind === "shabbat_yomtov") {
    return chagHe
      ? t("day_detail.candle_lighting_shabbat_chag", { chag: chagHe })
      : t("day_detail.candle_lighting_shabbat_chag_default");
  }
  if (kind === "yomtov") {
    return chagHe
      ? t("day_detail.candle_lighting_yomtov_chag", { chag: chagHe })
      : t("day_detail.candle_lighting_yomtov_default");
  }
  return t("day_detail.candle_lighting_shabbat");
}

/** Mirror of `entryLabel` for havdalah / chag exit. */
function exitLabel(shabbat, t) {
  if (!shabbat) return t("day_detail.exit_shabbat");
  const kind = shabbat.havdalah_for;
  const chagHe = shabbat.yomtov_he;
  if (kind === "shabbat_yomtov") {
    return chagHe
      ? t("day_detail.exit_shabbat_chag", { chag: chagHe })
      : t("day_detail.exit_shabbat_chag_default");
  }
  if (kind === "yomtov") {
    return chagHe
      ? t("day_detail.exit_yomtov_chag", { chag: chagHe })
      : t("day_detail.exit_yomtov_default");
  }
  return t("day_detail.exit_shabbat");
}


export default function DayDetailDialog({
  open, onClose, day, gregYear, gregMonth, gregDay, isAuthed,
  initialLocation = "jerusalem",
  personalEvents = [],         // pre-filtered to this day
  onPersonalChanged,           // notify parent so it can refetch the month
}) {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  // `location` may be a catalog id (back-compat: "jerusalem") or a
  // full descriptor object { kind:"custom", geonameid|lat/lon/tzid }.
  const [location, setLocation] = useState(initialLocation);
  const [zm, setZm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Reset to outer prop when modal opens
  useEffect(() => {
    if (open) setLocation(initialLocation);
  }, [open, initialLocation]);

  useEffect(() => {
    if (!open || !day) { setZm(null); return; }
    let cancelled = false;
    setLoading(true);
    // Build the API params from the location descriptor.
    let params;
    if (typeof location === "string") {
      params = { location };
    } else if (location?.kind === "catalog") {
      params = { location: location.id };
    } else if (location?.kind === "custom" && location.geonameid) {
      params = {
        geonameid: location.geonameid,
        name:      location.name,
        name_en:   location.name_en,
      };
    } else if (location?.kind === "custom" && location.latitude != null) {
      params = {
        lat:     location.latitude,
        lon:     location.longitude,
        tzid:    location.tzid,
        name:    location.name,
        name_en: location.name_en,
      };
    } else {
      params = { location: "jerusalem" };
    }
    api.calendarZmanim(day.date, params)
      .then((d) => { if (!cancelled) setZm(d); })
      .catch(() => { if (!cancelled) setZm(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, day?.date, location]);

  if (!day) return null;

  // Derive day-level metadata from the events array
  const events = day.events || [];
  const parsha = events.find((e) => e.category === "parsha");
  const significant = events.filter(
    (e) => e.category !== "parsha" && e.category !== "regular"
  );

  const dow = new Date(gregYear, gregMonth - 1, gregDay).getDay();
  // Hebrew labels come from the backend (Sefaria-grade transliteration
  // isn't a thing the FE should invent on its own). In Hebrew mode the
  // `labels` map is byte-identical to legacy. In English mode we swap
  // in the local i18n table so users see "Sunrise (Hanetz)" etc.
  const labels = isEn
    ? {
        alotHaShachar:   t("zmanim_labels.alotHaShachar"),
        misheyakir:      t("zmanim_labels.misheyakir"),
        sunrise:         t("zmanim_labels.sunrise"),
        sofZmanShmaMGA:  t("zmanim_labels.sofZmanShmaMGA"),
        sofZmanShmaGRA:  t("zmanim_labels.sofZmanShmaGRA"),
        sofZmanTfillaMGA:t("zmanim_labels.sofZmanTfillaMGA"),
        sofZmanTfillaGRA:t("zmanim_labels.sofZmanTfillaGRA"),
        chatzot:         t("zmanim_labels.chatzot"),
        minchaGedola:    t("zmanim_labels.minchaGedola"),
        minchaKetana:    t("zmanim_labels.minchaKetana"),
        plagHaMincha:    t("zmanim_labels.plagHaMincha"),
        sunset:          t("zmanim_labels.sunset"),
        tzeit:           t("zmanim_labels.tzeit"),
        rabbeinuTam:     t("zmanim_labels.rabbeinuTam"),
      }
    : (zm?.labels || {});

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto p-0"
          dir="rtl"
          data-testid="day-detail-dialog"
        >
          {/* Header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-gold/25 text-right">
            <div className="text-xs tracking-[0.2em] uppercase text-gold/80">
              {t(`calendar_page.weekday_long_${dow}`)}
            </div>
            <DialogTitle className="font-heading text-2xl text-foreground mt-1">
              {/* In Hebrew mode: backend-supplied gematria date (e.g.
                  "ד׳ בסיון תשפ״ו") — byte-identical to legacy. In
                  English mode: FE transliteration "4 Sivan 5786". */}
              {isEn
                ? formatHebrewDateEn(new Date(gregYear, gregMonth - 1, gregDay))
                : day.hebrew_date}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {/* Gregorian secondary line. Hebrew: "11 ביוני 2026"
                  (legacy format byte-identical). English: "June 11, 2026". */}
              {isEn
                ? formatDateForLang(new Date(gregYear, gregMonth - 1, gregDay), "en", { dateStyle: "medium" })
                : `${gregDay} ב${t(`calendar_page.greg_month_${gregMonth}`)} ${gregYear}`}
            </DialogDescription>
          </DialogHeader>

          {/* Significant events */}
          {(significant.length > 0 || parsha) && (
            <section className="px-6 py-4 border-b border-border/40">
              <div className="text-[10px] tracking-[0.25em] uppercase text-gold/80 mb-3 text-right">
                ✦ {t("day_detail.events_header")}
              </div>
              <ul className="space-y-1.5 text-right" data-testid="day-events">
                {significant.map((e, i) => {
                  const Icon = CATEGORY_ICON[e.category] || Sparkles;
                  return (
                    <li key={i} className="flex items-center justify-end gap-2 text-sm">
                      <span className="text-foreground">{e.title}</span>
                      <Icon className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                    </li>
                  );
                })}
                {parsha && (
                  <li className="flex items-center justify-end gap-2 text-sm">
                    <span className="text-foreground">{parsha.title}</span>
                    <CalIcon className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                  </li>
                )}
              </ul>
            </section>
          )}

          {/* Zmanim */}
          <section className="px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => setPickerOpen(true)}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"
                data-testid="day-change-location"
              >
                <MapPin className="h-3 w-3" strokeWidth={1.5} />
                {/* Backend currently only returns Hebrew location names.
                    English users still see the transliterated/Hebrew
                    name (e.g. "ירושלים") — translating city names is a
                    backend concern handled in a separate session. */}
                {zm?.location_he || t("day_detail.default_location_he")}
                <span className="text-gold">[{t("day_detail.change_location")}]</span>
              </button>
              <div className="text-[10px] tracking-[0.25em] uppercase text-gold/80">
                ✦ {t("day_detail.zmanim_header")}
              </div>
            </div>

            {loading ? (
              <div className="grid place-items-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-gold" />
              </div>
            ) : !zm ? (
              <div className="text-center text-xs text-muted-foreground py-6">
                {t("day_detail.no_zmanim")}
              </div>
            ) : (
              <Tabs defaultValue="day" dir="rtl" data-testid="zmanim-tabs">
                <TabsList className="w-full grid grid-cols-2 mb-3">
                  <TabsTrigger value="day"     data-testid="tab-zmanim-day"    >{t("day_detail.tab_day")}</TabsTrigger>
                  <TabsTrigger value="shabbat" data-testid="tab-zmanim-shabbat">{t("day_detail.tab_shabbat")}</TabsTrigger>
                </TabsList>
                <TabsContent value="day" className="mt-0">
                  <div className="rounded-md bg-card/30 px-3 py-2" data-testid="zmanim-day-panel">
                    {ZMANIM_DAY.map((k) => (
                      <ZmanRow
                        key={k}
                        label={labels[k] || k}
                        value={zm.zmanim?.[k]}
                        testid={`zman-${k}`}
                      />
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="shabbat" className="mt-0">
                  <div className="rounded-md bg-card/30 px-3 py-3 space-y-3" data-testid="zmanim-shabbat-panel">
                    {zm.shabbat?.candles_lighting ? (
                      <div className="flex items-center justify-between text-sm" data-testid="row-candles">
                        <span className="font-mono">{zm.shabbat.candles_lighting}</span>
                        <span className="text-muted-foreground flex items-center gap-1.5">
                          <Sun className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                          {entryLabel(zm.shabbat, t)}
                        </span>
                      </div>
                    ) : null}
                    {zm.shabbat?.havdalah ? (
                      <div className="flex items-center justify-between text-sm" data-testid="row-havdalah">
                        <span className="font-mono">{zm.shabbat.havdalah}</span>
                        <span className="text-muted-foreground flex items-center gap-1.5">
                          <Moon className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                          {exitLabel(zm.shabbat, t)}
                        </span>
                      </div>
                    ) : null}
                    {!zm.shabbat?.candles_lighting && !zm.shabbat?.havdalah && (
                      <div className="text-xs text-muted-foreground text-center py-2">
                        {t("day_detail.no_shabbat_chag")}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            )}
            {/* Disclaimer — zmanim are an aid, not a halachic authority.
                Source/algorithm differences (sea-level vs city elevation,
                MGA vs GRA, civil vs nautical dusk) can shift the time by
                a few minutes — and a few minutes matter. */}
            {!loading && zm && (
              <p
                className="mt-3 text-[10.5px] sm:text-[11px] leading-snug text-muted-foreground/80 text-center"
                data-testid="zmanim-disclaimer"
              >
                {t("day_detail.disclaimer")}
              </p>
            )}
          </section>

          {/* Personal events — visible only to logged-in users. */}
          <section className="px-6 py-4 border-t border-border/40">
            <div className="flex items-center justify-between mb-3">
              {isAuthed && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-gold/40 text-gold hover:bg-gold/10"
                  onClick={() => { setEditing(null); setFormOpen(true); }}
                  data-testid="add-personal-event-btn"
                >
                  <Plus className="h-3.5 w-3.5 ml-1" strokeWidth={1.5} />
                  {t("day_detail.add_personal_event")}
                </Button>
              )}
              <div className="text-[10px] tracking-[0.25em] uppercase text-gold/80 text-right">
                ✦ {t("day_detail.personal_events_header")}
              </div>
            </div>
            {!isAuthed ? (
              <div
                className="rounded-md border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground"
                data-testid="personal-events-anon"
              >
                {t("day_detail.personal_anon")}
              </div>
            ) : personalEvents.length === 0 ? (
              <div
                className="rounded-md border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground"
                data-testid="personal-events-empty"
              >
                {t("day_detail.personal_empty")}
              </div>
            ) : (
              <ul className="space-y-2" data-testid="personal-events-list">
                {personalEvents.map((ev) => (
                  <li
                    key={`${ev.id}-${ev.occurrence_date}`}
                    className="flex items-start justify-between gap-2 rounded-md bg-card/40 border border-border/40 p-2.5"
                    data-testid={`personal-event-${ev.id}`}
                  >
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setEditing(ev); setFormOpen(true); }}
                        className="p-1 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground"
                        aria-label={t("day_detail.edit_label")}
                        data-testid={`edit-event-${ev.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(t("day_detail.delete_confirm"))) return;
                          try {
                            await api.myEventDelete(ev.id);
                            toast.success(t("day_detail.deleted_toast"));
                            onPersonalChanged?.();
                          } catch {
                            toast.error(t("day_detail.delete_failed"));
                          }
                        }}
                        className="p-1 rounded hover:bg-rose-100/40 text-muted-foreground hover:text-rose-600"
                        aria-label={t("day_detail.delete_label")}
                        data-testid={`delete-event-${ev.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="flex-1 text-right min-w-0">
                      <div className="flex items-center justify-end gap-2">
                        <span className="font-medium text-sm text-foreground truncate">{ev.title}</span>
                        <span
                          className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${COLOR_DOT[ev.color] || "bg-gold/80"}`}
                          aria-hidden
                        />
                      </div>
                      {ev.time && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center justify-end gap-1">
                          <Clock className="h-3 w-3" strokeWidth={1.5} />
                          {ev.time}
                        </div>
                      )}
                      {ev.description && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{ev.description}</div>
                      )}
                      {ev.is_adar_shifted && (
                        <div
                          className="text-[10px] text-gold mt-1 flex items-center justify-end gap-1"
                          data-testid={`adar-shifted-${ev.id}`}
                        >
                          <AlertCircle className="h-3 w-3" strokeWidth={1.5} />
                          {t("day_detail.adar_shifted")}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </DialogContent>
      </Dialog>

      <LocationPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        current={location}
        onPick={setLocation}
        isAuthed={isAuthed}
      />

      <EventForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        initialDate={day?.date}
        existing={editing}
        onSaved={() => onPersonalChanged?.()}
      />
    </>
  );
}
